/**
 * Normalize agent / WebSocket / Cart API cart payloads into CartContext line items.
 */

function pickProductId(row) {
  if (!row || typeof row !== "object") return null;
  const raw =
    row.product_id ??
    row.article_id ??
    row.id ??
    row.sku ??
    row.articleId;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s === "" ? null : s;
}

function parseQuantity(row) {
  const q =
    row.quantity ??
    row.qty ??
    row.quantity_ordered ??
    row.count ??
    row.amount;
  if (typeof q === "number" && Number.isFinite(q) && q >= 1) return Math.floor(q);
  const n = parseInt(String(q ?? 1), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function parseUnitPrice(row) {
  const p =
    row.unit_price ??
    row.price ??
    row.sale_price ??
    row.item_price ??
    row.unitPrice ??
    row.price_info?.price;
  if (typeof p === "number" && Number.isFinite(p)) return p;
  const f = parseFloat(String(p ?? 0));
  return Number.isFinite(f) ? f : 0;
}

function pickTitle(row) {
  return (
    (typeof row.title === "string" && row.title) ||
    (typeof row.product_title === "string" && row.product_title) ||
    (typeof row.name === "string" && row.name) ||
    ""
  );
}

function pickImage(row) {
  const direct =
    (typeof row.image_uri === "string" && row.image_uri) ||
    (typeof row.image === "string" && row.image) ||
    (typeof row.uri === "string" && row.uri) ||
    "";
  if (direct) return direct;
  if (Array.isArray(row.images) && row.images.length > 0) {
    const first = row.images[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object" && typeof first.url === "string") {
      return first.url;
    }
  }
  return "";
}

function pickCurrency(row) {
  const c =
    row.currency ??
    row.currency_code ??
    row.currencyCode ??
    row.price_info?.currencyCode;
  return typeof c === "string" && c.trim() ? c.trim() : "USD";
}

function pickSize(row) {
  const s = row.size ?? row.selected_size ?? row.Size;
  if (s != null && s !== "") {
    if (Array.isArray(s)) return s[0] != null ? String(s[0]) : null;
    return String(s);
  }
  const attrSize = row.attributes?.size;
  if (Array.isArray(attrSize) && attrSize.length > 0 && attrSize[0] != null) {
    return String(attrSize[0]);
  }
  if (typeof attrSize === "string" && attrSize) return attrSize;
  return null;
}

/**
 * @param {unknown} payload — array of lines or `{ items }` / `{ cart: { items } }`
 * @returns {Record<string, unknown>[]}
 */
export function normalizeAgentCartPayload(payload) {
  if (payload == null) return [];

  /** @type {unknown[]} */
  let rows = [];

  if (Array.isArray(payload)) {
    rows = payload;
  } else if (typeof payload === "object") {
    const o = /** @type {Record<string, unknown>} */ (payload);
    const nestedCart =
      o.cart != null && typeof o.cart === "object" && !Array.isArray(o.cart)
        ? /** @type {Record<string, unknown>} */ (o.cart)
        : null;
    const cand =
      o.items ??
      (nestedCart && Array.isArray(nestedCart.items)
        ? nestedCart.items
        : null) ??
      o.lines ??
      o.cart_items ??
      o.cart_data ??
      o.entries ??
      o.products;
    if (Array.isArray(cand)) rows = cand;
    else return [];
  } else {
    return [];
  }

  const out = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const id = pickProductId(row);
    if (!id) continue;

    const size = pickSize(row);
    const img = pickImage(row);

    out.push({
      id,
      title: pickTitle(row),
      price: parseUnitPrice(row),
      currency: pickCurrency(row),
      description:
        typeof row.description === "string" ? row.description : "",
      image_uri: img,
      image: img,
      attributes: size ? { size: [size] } : {},
      variants: [],
      quantity: parseQuantity(row),
      size,
    });
  }
  return out;
}

/**
 * Extract a cart snapshot from a full agent `lastResponse`.
 * Returns `[]` for clear/purchase-complete, a payload for replace, or `null` if none.
 *
 * @param {unknown} lastResponse
 * @returns {unknown[] | Record<string, unknown> | null}
 */
export function extractCartPayloadFromAgentResponse(lastResponse) {
  if (!lastResponse || typeof lastResponse !== "object") return null;

  const lr = /** @type {Record<string, unknown>} */ (lastResponse);
  const action =
    lr.action != null && typeof lr.action === "object" && !Array.isArray(lr.action)
      ? /** @type {Record<string, unknown>} */ (lr.action)
      : null;
  const actionType = action?.type != null ? String(action.type) : "";

  const intentRaw = lr.intent;
  const intents = Array.isArray(intentRaw)
    ? intentRaw.map(String)
    : intentRaw != null && intentRaw !== ""
      ? [String(intentRaw)]
      : [];

  // Empty cart outcomes
  if (
    actionType === "CLEAR_CART" ||
    actionType === "PURCHASE_COMPLETE" ||
    lr.purchase_completed === true ||
    intents.some(
      (i) => i.includes("clear_cart") || i.includes("confirm_purchase")
    )
  ) {
    return [];
  }

  // Prefer nested cart summaries (ADD_TO_CART / VIEW_CART / PREPARE_BILLING)
  if (lr.cart != null && typeof lr.cart === "object") return lr.cart;
  const cartMutation =
    lr.cart_mutation != null && typeof lr.cart_mutation === "object"
      ? /** @type {Record<string, unknown>} */ (lr.cart_mutation)
      : null;
  if (cartMutation?.cart != null && typeof cartMutation.cart === "object") {
    return cartMutation.cart;
  }
  if (action?.cart != null && typeof action.cart === "object") return action.cart;
  if (Array.isArray(action?.cart_data)) return action.cart_data;

  // VIEW_CART sometimes puts lines at the top level
  if (
    (lr.cart_view === true ||
      actionType === "VIEW_CART" ||
      intents.some((i) => i.includes("view_cart"))) &&
    Array.isArray(lr.items)
  ) {
    return lr.items;
  }

  return null;
}

/**
 * Apply agent lastResponse cart to UI via replaceCart (no Cart API).
 * @param {(payload: unknown) => boolean} replaceCart
 * @param {unknown} lastResponse
 */
export function applyCartFromAgentResponse(replaceCart, lastResponse) {
  if (typeof replaceCart !== "function") return false;
  const payload = extractCartPayloadFromAgentResponse(lastResponse);
  if (payload === null) return false;
  try {
    return Boolean(replaceCart(payload));
  } catch {
    return false;
  }
}

/**
 * @param {(payload: unknown) => boolean} replaceCart returns true when applied
 * @param {unknown} action
 */
export function tryReplaceCartFromAgentAction(replaceCart, action) {
  if (typeof replaceCart !== "function" || !action || typeof action !== "object")
    return false;

  if (action.type === "CLEAR_CART" || action.type === "PURCHASE_COMPLETE") {
    try {
      replaceCart([]);
      return true;
    } catch {
      /* ignore */
    }
  }

  /** @type {unknown[]} */
  const candidates = [action.cart, action.cart_snapshot];
  const cd =
    /** @type {{ cart_data?: unknown }} */ (action).cart_data;
  if (Array.isArray(cd)) candidates.push(cd);

  for (const c of candidates) {
    if (c == null) continue;
    try {
      if (replaceCart(c)) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}
